// CreditNova Zod Schemas
import { z } from 'zod';

// ===========================================
// Re-export Extraction, Report, and Agent Schemas
// ===========================================

export * from './extraction.js';
export * from './report.js';
export * from './agent.js';
export * from './document-integrity.js';

// ===========================================
// Common Schemas
// ===========================================

export const uuidSchema = z.string().uuid();

export const paginationSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});

// ===========================================
// User Schemas
// ===========================================

export const userRoleSchema = z.enum(['admin', 'credit_analyst', 'viewer']);
export const userStatusSchema = z.enum(['active', 'inactive', 'suspended']);
export const preferredModeSchema = z.enum(['direct', 'api']).nullable().optional();

export const createUserSchema = z.object({
  email: z.string().email(),
  firstName: z.string().min(1).max(100).optional(),
  lastName: z.string().min(1).max(100).optional(),
  role: userRoleSchema.default('viewer'),
});

export const updateUserSchema = z.object({
  firstName: z.string().min(1).max(100).optional(),
  lastName: z.string().min(1).max(100).optional(),
  role: userRoleSchema.optional(),
  status: userStatusSchema.optional(),
  preferredMode: preferredModeSchema,
});

// ===========================================
// Document Schemas
// ===========================================

export const documentTypeSchema = z.enum([
  'financial_statement',
  'bank_statement',
  'credit_report',
  'corporate_search',
  'other',
]);

export const documentStatusSchema = z.enum([
  'pending',
  'processing',
  'completed',
  'failed',
]);

export const uploadDocumentSchema = z.object({
  documentType: documentTypeSchema,
  metadata: z.record(z.unknown()).optional(),
});

// ===========================================
// Workflow State Schema
// ===========================================

// Canonical workflow lifecycle states shared by both frontend and backend.
// The analyses.status column stores the SAME values so the two stay in sync.
//
//   idle                → workflow created, nothing uploaded yet
//   uploading           → files are being sent to the server
//   processing          → BullMQ jobs are running (polling active)
//   financial_review    → financial extraction done, awaiting user overrides
//   metrics_review      → overrides confirmed, awaiting user metrics selection
//   processing_remaining→ user completed overrides/metrics, other jobs still running
//   generating          → all extractions done, report generation in progress
//   complete            → report generated successfully
export const workflowStateSchema = z.enum([
  'idle',
  'uploading',
  'processing',
  'financial_review',
  'metrics_review',
  'processing_remaining',
  'generating',
  'complete',
]);

// ===========================================
// Analysis Schemas
// ===========================================

// Analysis status includes all workflow states plus 'archived' for soft-delete
export const analysisStatusSchema = z.enum([
  'idle',
  'uploading',
  'processing',
  'financial_review',
  'metrics_review',
  'processing_remaining',
  'generating',
  'complete',
  'archived',
]);

export const creditDecisionSchema = z.enum(['approve', 'conditional', 'decline']);
export const riskLevelSchema = z.enum(['critical', 'high', 'medium', 'low']);
export const significanceSchema = z.enum(['high', 'medium', 'low']);

export const createAnalysisSchema = z.object({
  businessName: z.string().min(1).max(255),
  writeupType: z.string().optional(),
  workflowId: z.string().uuid().optional(),
  documentIds: z.array(uuidSchema).min(1),
});

export const updateAnalysisSchema = z.object({
  businessName: z.string().min(1).max(255).optional(),
  writeupType: z.string().optional(),
  status: analysisStatusSchema.optional(),
});

// ===========================================
// AI Analysis Schemas
// ===========================================

export const findingSchema = z.object({
  finding: z.string(),
  sourceReference: z.string().describe('Page number or section reference'),
  significance: significanceSchema.optional(),
  riskLevel: riskLevelSchema.optional(),
});

export const creditAssessmentSchema = z.object({
  recommendation: creditDecisionSchema,
  rationale: z.string(),
  conditions: z.array(z.string()).optional(),
});

export const aiAnalysisSchema = z.object({
  executiveSummary: z.string().describe('2-3 sentence summary of credit worthiness'),
  strengths: z.array(
    z.object({
      finding: z.string(),
      sourceReference: z.string().describe('Page number or section reference'),
      significance: significanceSchema,
    })
  ),
  concerns: z.array(
    z.object({
      finding: z.string(),
      sourceReference: z.string(),
      riskLevel: riskLevelSchema,
    })
  ),
  recommendations: z.array(z.string()),
  creditDecision: creditAssessmentSchema,
});

// ===========================================
// Template Schemas
// ===========================================

export const templateConfigSchema = z.object({
  // New Modular Structure
  global: z.object({
    writeupType: z.string(),
    summaryStyle: z.string(),
    summaryLength: z.enum(['brief', 'standard', 'detailed', 'extensive']),
    customInstructions: z.string().optional(),
  }).optional(),

  modules: z.object({
    financial: z.object({
      enabled: z.boolean(),
      statementType: z.string().optional(),
      selectedMetricIds: z.array(z.string()),
      customRatios: z.array(z.any()),
      offBalanceSheet: z.array(z.any()),
    }).optional(),
    creditReport: z.object({
      enabled: z.boolean(),
      inclusionMode: z.enum(['automatic', 'manual']).optional(),
      selectedKeys: z.any().optional(),
    }).optional(),
    bank: z.object({
      enabled: z.boolean(),
      type: z.string().optional(),
    }).optional(),
    corporate: z.object({
      enabled: z.boolean(),
      additionalFields: z.record(z.string(), z.any()).optional(),
    }).optional(),
  }).optional(),

  // Keep legacy fields as optional so old templates don't break
  sections: z.array(z.string()).optional(),
  ratios: z.array(z.string()).optional(),
  writeupType: z.string().optional(), // Top level legacy
  selectedMetrics: z.array(z.string()).optional(),
}).passthrough(); // .passthrough() allows keys we might have missed to stay in the object

export const createTemplateSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
  config: templateConfigSchema,
  isDefault: z.boolean().default(false),
  workflowId: z.string().uuid().optional(),
});

export const updateTemplateSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().max(1000).optional(),
  config: templateConfigSchema.optional(),
  isDefault: z.boolean().optional(),
});

// ===========================================
// Financial Input Schemas
// ===========================================

export const financialInputsSchema = z.object({
  currentAssets: z.string().optional(),
  currentLiabilities: z.string().optional(),
  inventory: z.string().optional(),
  totalDebt: z.string().optional(),
  totalEquity: z.string().optional(),
  intangibles: z.string().optional(),
  operatingIncome: z.string().optional(),
  depreciation: z.string().optional(),
  amortization: z.string().optional(),
  rent: z.string().optional(),
  interest: z.string().optional(),
  principal: z.string().optional(),
  netIncome: z.string().optional(),
  revenue: z.string().optional(),
  grossProfit: z.string().optional(),
  cogs: z.string().optional(),
  avgInventory: z.string().optional(),
  avgReceivables: z.string().optional(),
  sales: z.string().optional(),
});

// ===========================================
// Job Schemas
// ===========================================

export const jobStatusSchema = z.enum(['pending', 'active', 'completed', 'failed']);
export const jobTypeSchema = z.enum([
  'extract-financial',
  'extract-bank',
  'extract-credit-report',
  'ai-analysis',
]);

// ===========================================
// Type Exports from Schemas
// ===========================================

export type UserRole = z.infer<typeof userRoleSchema>;
export type UserStatus = z.infer<typeof userStatusSchema>;
export type CreateUserInput = z.infer<typeof createUserSchema>;
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

export type DocumentType = z.infer<typeof documentTypeSchema>;
export type DocumentStatus = z.infer<typeof documentStatusSchema>;
export type UploadDocumentInput = z.infer<typeof uploadDocumentSchema>;

export type WorkflowState = z.infer<typeof workflowStateSchema>;
export type AnalysisStatus = z.infer<typeof analysisStatusSchema>;
export type CreditDecision = z.infer<typeof creditDecisionSchema>;
export type CreateAnalysisInput = z.infer<typeof createAnalysisSchema>;
export type UpdateAnalysisInput = z.infer<typeof updateAnalysisSchema>;

export type AIAnalysisOutput = z.infer<typeof aiAnalysisSchema>;

export type TemplateConfig = z.infer<typeof templateConfigSchema>;
export type CreateTemplateInput = z.infer<typeof createTemplateSchema>;
export type UpdateTemplateInput = z.infer<typeof updateTemplateSchema>;

export type FinancialInputs = z.infer<typeof financialInputsSchema>;

export type JobStatus = z.infer<typeof jobStatusSchema>;
export type JobType = z.infer<typeof jobTypeSchema>;

